import { z } from "zod";

// TES-330 / TES-296 (Swedish PDL) — access = role (RolesGuard) + an active
// care relationship (CareRelationship) + purpose (AccessLog.purpose). Every
// read of a specific patient's data by an employee is recorded in the access
// log.

// How a care relationship arose, strongest → weakest.
export const CARE_RELATIONSHIP_BASES = [
  "meeting", // a booked consult binds doctor↔patient
  "clinical_action", // prescribed / reviewed labs / etc.
  // TES-804 (Ö-224) — a doctor whose access ENDED at a handover, re-opening it
  // himself for a stated reason and a bounded time. Ranked above
  // `self_asserted` because it is a deliberate, reasoned act rather than one
  // the guard inferred from a click — and below the two that come from real
  // clinical work, which it is not.
  "reinstated",
  // TES-977 — the patient handed in something a doctor must review (F-02,
  // F-07, the monthly check-in, a renewal), so every active doctor in the
  // tenant is in care with her before he opens her. Above `self_asserted`
  // because she asked for care rather than the guard inferring it from a
  // click; below `reinstated`, which must stay untouched by a hand-in so the
  // handover rule keeps reading an expired reinstatement as "relieved".
  "review_requested",
  // TES-1123 — a coach opening a patient whose subscription is trialing or
  // active: the subscription is what puts the coach in her care. A documented
  // ground, so it raises no access alarm; ranked just above `self_asserted`.
  "coaching",
  "self_asserted", // established by the act of access (logged)
  "clinic", // same partner clinic (weakest)
] as const;
export const careRelationshipBasisSchema = z.enum(CARE_RELATIONSHIP_BASES);
export type CareRelationshipBasis = z.infer<typeof careRelationshipBasisSchema>;

// TES-1040 — the bases an ACCESS-LOG row may carry: every care-relationship
// basis, plus `support` — an admin's reasoned, day-bounded support access
// (support-access.ts). `support` is deliberately not a care-relationship
// basis: an admin never gets a care relationship, the grant is its own
// record (SupportAccessGrant).
export const ACCESS_LOG_BASES = [
  ...CARE_RELATIONSHIP_BASES,
  "support",
] as const;
export type AccessLogBasis = (typeof ACCESS_LOG_BASES)[number];

// Syfte — the PDL purpose of an access. `care` is the default; the others
// exist for future non-care access paths (quality review, administration).
export const ACCESS_PURPOSES = [
  "care",
  "quality_assurance",
  "administration",
] as const;
export const accessPurposeSchema = z.enum(ACCESS_PURPOSES);
export type AccessPurpose = z.infer<typeof accessPurposeSchema>;
export const DEFAULT_ACCESS_PURPOSE: AccessPurpose = "care";

// TES-804 (Ö-224) — a doctor re-opening access he lost at a handover.
//
// Emelie: "Tillfällig återåtkomst kan ges vid legitimt vårdbehov, med obligatorisk orsak, tidsgräns och auditlogg."
// All three are required here, and the patient travels in the BODY rather
// than the path on purpose — see the controller.
export const REINSTATE_ACCESS_MAX_HOURS = 24;
export const reinstateAccessSchema = z.object({
  patientId: z.string().min(1),
  // Compulsory, and trimmed before the length check so a field of spaces
  // cannot pass for a reason.
  reason: z.string().trim().min(1).max(2000),
  // The window. Bounded at a day: "tillfällig" is the word, and a limit the
  // caller can set to a year is not a limit. He may ask again — each request
  // is its own audited row, which is the record that matters.
  hours: z.number().int().min(1).max(REINSTATE_ACCESS_MAX_HOURS),
});
export type ReinstateAccessRequest = z.infer<typeof reinstateAccessSchema>;

// One access-log row, as surfaced to an admin / to the patient's own
// "who read my journal" view.
export const accessLogEntrySchema = z.object({
  id: z.string(),
  employeeId: z.string(),
  employeeRole: z.string(),
  purpose: z.string(),
  relationshipBasis: z.string(),
  hadActiveRelationship: z.boolean(),
  route: z.string(),
  occurredAt: z.string(),
});
export type AccessLogEntry = z.infer<typeof accessLogEntrySchema>;

export const patientAccessLogSchema = z.object({
  patientId: z.string(),
  entries: z.array(accessLogEntrySchema),
});
export type PatientAccessLog = z.infer<typeof patientAccessLogSchema>;
